import React, { useState, useEffect } from 'react';
import './ZoneZappCard.css';

const CRM_API_BASE = process.env.REACT_APP_CRM_API_URL || 'http://localhost:6001/api';

export default function ZoneZappCard({ location }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30); // 7, 14, or 30 days

  useEffect(() => {
    if (location) {
      fetchZoneAnalytics();
    }
  }, [location, days]);

  const fetchZoneAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Accept': 'application/json'
      };

      // First, get all zones
      const zonesRes = await fetch(`${CRM_API_BASE}/zones/list?limit=100`, {
        headers
      });

      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        const zones = zonesData.data?.zones || zonesData.zones || [];
        
        console.log('[ZoneZappCard] Total zones available:', zones.length);
        console.log('[ZoneZappCard] User location:', location);
        
        if (zones && zones.length > 0) {
          const userLat = location.latitude || location.lat;
          const userLon = location.longitude || location.lon || location.lng;
          
          if (!userLat || !userLon) {
            console.log('[ZoneZappCard] No user coordinates available');
            return;
          }

          // Find the nearest zone by calculating distance
          const calculateDistance = (lat1, lon1, lat2, lon2) => {
            const R = 6371; // Earth's radius in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = 
              Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            return R * c; // Distance in km
          };

          let nearestZone = null;
          let minDistance = Infinity;

          zones.forEach(zone => {
            const zoneLat = zone.coordinates?.latitude;
            const zoneLon = zone.coordinates?.longitude;
            
            if (zoneLat && zoneLon) {
              const distance = calculateDistance(userLat, userLon, zoneLat, zoneLon);
              if (distance < minDistance) {
                minDistance = distance;
                nearestZone = zone;
              }
            }
          });
          
          console.log('[ZoneZappCard] Nearest zone:', nearestZone?.name || nearestZone?.address, 'Distance:', minDistance.toFixed(2), 'km');
          
          if (nearestZone) {
            const zoneId = nearestZone._id || nearestZone.id;
            
            // Fetch analytics for this zone
            const analyticsRes = await fetch(
              `${CRM_API_BASE}/analytics/zone/${zoneId}/summary?days=${days}`,
              {
                headers
              }
            );

            if (analyticsRes.ok) {
              const data = await analyticsRes.json();
              console.log('[ZoneZappCard] Analytics data:', data);
              setAnalyticsData(data.data || data);
            } else {
              console.log('Analytics not available for this zone');
            }
          } else {
            console.log('[ZoneZappCard] No nearby zones found');
          }
        } else {
          console.log('No zones available');
        }
      } else {
        console.log('Failed to fetch zones list');
      }
    } catch (err) {
      console.error('Error fetching zone analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="zone-zapp-card">
        <h2 className="section-title">Zone's Zapp</h2>
        <div className="zone-zapp-loading">
          <div className="loading-spinner"></div>
          <p>Loading zone insights...</p>
        </div>
      </section>
    );
  }

  if (error || !analyticsData) {
    return null; // Don't show the card if there's no data
  }

  const { metrics, summary, topics, sentiment, engagementLevel } = analyticsData;
  
  // Calculate sentiment percentages
  const sentimentTotal = (sentiment?.positive || 0) + (sentiment?.neutral || 0) + (sentiment?.negative || 0);
  const positivePercent = sentimentTotal > 0 ? Math.round((sentiment.positive / sentimentTotal) * 100) : 0;
  const neutralPercent = sentimentTotal > 0 ? Math.round((sentiment.neutral / sentimentTotal) * 100) : 0;
  const negativePercent = sentimentTotal > 0 ? Math.round((sentiment.negative / sentimentTotal) * 100) : 0;

  // Engagement level styling
  const engagementClass = 
    engagementLevel === 'HIGH ENGAGEMENT' || engagementLevel === 'high' ? 'engagement-high' :
    engagementLevel === 'MEDIUM ENGAGEMENT' || engagementLevel === 'medium' ? 'engagement-medium' :
    'engagement-low';

  return (
    <section className="zone-zapp-card">
      <div className="zone-zapp-header">
        <h2 className="section-title">Zone's Zapp</h2>
        <span className={`engagement-badge ${engagementClass}`}>
          {engagementLevel || 'LOW ENGAGEMENT'}
        </span>
      </div>

      {/* Time Range Selector */}
      <div className="time-range-selector">
        <button 
          className={days === 7 ? 'active' : ''} 
          onClick={() => setDays(7)}
        >
          7 Days
        </button>
        <button 
          className={days === 14 ? 'active' : ''} 
          onClick={() => setDays(14)}
        >
          14 Days
        </button>
        <button 
          className={days === 30 ? 'active' : ''} 
          onClick={() => setDays(30)}
        >
          30 Days
        </button>
      </div>

      {/* Key Metrics */}
      <div className="zone-metrics">
        <div className="metric-card">
          <div className="metric-value">{metrics?.totalMessages || 0}</div>
          <div className="metric-label">Total Messages</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{metrics?.activeUsers || 0}</div>
          <div className="metric-label">Active Users</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{metrics?.avgMessagesPerDay || 0}</div>
          <div className="metric-label">Avg Messages/Day</div>
        </div>
      </div>

      {/* AI Summary */}
      {summary && (
        <div className="zone-summary">
          <div className="summary-header">
            <span className="ai-icon">🤖</span>
            <span className="summary-title">AI Summary</span>
          </div>
          <p className="summary-text">
            {summary.length > 200 ? summary.substring(0, 200) + '...' : summary}
          </p>
        </div>
      )}

      {/* Topics */}
      {topics && topics.length > 0 && (
        <div className="zone-topics">
          <h4 className="topics-title">Key Topics</h4>
          <div className="topics-list">
            {topics.slice(0, 5).map((topic, idx) => (
              <span key={idx} className="topic-tag">{topic}</span>
            ))}
          </div>
        </div>
      )}

      {/* Sentiment */}
      {sentimentTotal > 0 && (
        <div className="zone-sentiment">
          <h4 className="sentiment-title">Community Sentiment</h4>
          <div className="sentiment-bars">
            {positivePercent > 0 && (
              <div className="sentiment-bar">
                <div className="sentiment-label">Positive</div>
                <div className="sentiment-progress">
                  <div 
                    className="sentiment-fill positive" 
                    style={{ width: `${positivePercent}%` }}
                  ></div>
                  <span className="sentiment-percent">{positivePercent}%</span>
                </div>
              </div>
            )}
            {neutralPercent > 0 && (
              <div className="sentiment-bar">
                <div className="sentiment-label">Neutral</div>
                <div className="sentiment-progress">
                  <div 
                    className="sentiment-fill neutral" 
                    style={{ width: `${neutralPercent}%` }}
                  ></div>
                  <span className="sentiment-percent">{neutralPercent}%</span>
                </div>
              </div>
            )}
            {negativePercent > 0 && (
              <div className="sentiment-bar">
                <div className="sentiment-label">Negative</div>
                <div className="sentiment-progress">
                  <div 
                    className="sentiment-fill negative" 
                    style={{ width: `${negativePercent}%` }}
                  ></div>
                  <span className="sentiment-percent">{negativePercent}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
